import React from 'react';
import PropTypes from 'prop-types';
import { MetricsPicker } from '@bingads-webui-campaign-react/metrics-picker';
import { GranularityPicker } from '@bingads-webui-campaign-react/granularity-picker';
import { HelpPopup } from '@bingads-webui-react/help-popup';

/**
 * React component that renders a Chart Panel.
 * @param {Object} props - Properties used to render the component.
 * @returns {Element} React element.
 */
export class ChartPanel extends React.PureComponent {
  render() {
    // Moving average can be enabled but not active/used due to granularity not being daily.
    const isPopOrMovingAvg = (this.props.isMovingAverage && this.props.isMovingAverageVisible)
      || this.props.isPopEnabled;
    const vsString = this.props.i18n.getString('InlineChart_Metric_Vs');
    const showMovingAvgString = this.props.i18n.getString('BI_Chart_Show_MovingAverage');

    return (
      <div className="flex-chart-panel">
        <div className="chart-panel-right">
          <div className="metrics-pickers">
            <MetricsPicker
              selectedItem={this.props.primaryMetric.selectedItem}
              allMetrics={this.props.primaryMetric.metricsList}
              onChangeHandler={this.props.primaryMetric.selectionChangeCallback}
              isPopOrMovingAvg={isPopOrMovingAvg}
              selectedItemPercentage={this.props.primaryMetric.percentage}
              class="primary-metric"
              i18n={this.props.i18n}
            />
            <div className="metrics-vs">
              {vsString}
            </div>
            <MetricsPicker
              selectedItem={this.props.secondaryMetric.selectedItem}
              allMetrics={this.props.secondaryMetric.metricsList}
              onChangeHandler={this.props.secondaryMetric.selectionChangeCallback}
              isPopOrMovingAvg={isPopOrMovingAvg}
              selectedItemPercentage={this.props.secondaryMetric.percentage}
              class="secondary-metric"
              i18n={this.props.i18n}
            />
          </div>

          <GranularityPicker
            selectedItem={this.props.granularity.selectedItem}
            allGranularities={this.props.granularity.granularityList}
            onChangeHandler={this.props.granularity.selectionChangeCallback}
            class="granularity-picker"
            i18n={this.props.i18n}
          />
          {this.props.isMovingAverageVisible &&
            <div className="moving-average">
              <label
                htmlFor="isMovingAverage"
              >
                <input
                  id="isMovingAverage"
                  name="isMovingAverage"
                  type="checkbox"
                  checked={this.props.isMovingAverage}
                  onChange={this.props.movingAverageChangeCallback}
                />
                {showMovingAvgString}
              </label>
              <HelpPopup
                aria-label={this.props.i18n.getString('LearnMoreLinkText')}
                topic="pop_BA_PerformanceMovingAverage"
              />
            </div>}
        </div>
      </div >
    );
  }
}

ChartPanel.propTypes = {
  /** i18n */
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,

  /** If Pop and Moving average comparison are enabled */
  isPopEnabled: PropTypes.bool.isRequired,
  isMovingAverage: PropTypes.bool.isRequired,
  isMovingAverageVisible: PropTypes.bool.isRequired,
  /** Primary metric props used to drive the primary metric picker component */
  primaryMetric: PropTypes.shape({
    metricsList: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    selectedItem: PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    percentage: PropTypes.string.isRequired,
    selectionChangeCallback: PropTypes.func.isRequired,
  }).isRequired,

  /** Secondary metric props used to drive the secondary metric picker component */
  secondaryMetric: PropTypes.shape({
    metricsList: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    selectedItem: PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    percentage: PropTypes.string.isRequired,
    selectionChangeCallback: PropTypes.func.isRequired,
  }).isRequired,

  /** Granularity props used to drive the granularity picker component */
  granularity: PropTypes.shape({
    granularityList: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    selectedItem: PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    selectionChangeCallback: PropTypes.func.isRequired,
  }).isRequired,

  /** moving average prop */
  movingAverageChangeCallback: PropTypes.func.isRequired,
};
